import apiClient from './apiClient';
import { UserPreferences, Route, WheelchairSpecs } from '../types';

/**
 * Route API interfaces
 */
export interface RouteRequest {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  preferences: UserPreferences;
}

export interface RouteResponse {
  route: Route;
  alternatives?: Route[];
}

/**
 * Feedback interface
 */
export interface RouteFeedback {
  routeId: string;
  rating: number;
  selectedTags: string[];
  additionalComments?: string;
}

/**
 * Backend API Service
 * This service will connect to your future backend API
 * Simple send/retrieve data operations without authentication
 */
class BackendApiService {
  
  // ==================== User Preferences ====================

  /**
   * Create user entries in the backend based on wheelchair specs
   * Maps the internal WheelchairSpecs structure to the backend /user payload.
   * Returns the user ID from the response (uses the first/latest user ID if multiple wheelchairs).
   */
  async createUsersFromWheelchairs(wheelchairs: WheelchairSpecs[]): Promise<number | null> {
    const timestamp = new Date().toISOString();

    // Fire requests in parallel but wait for all to complete
    const responses = await Promise.all(
      wheelchairs.map((wheelchair) =>
        apiClient.post('/user', {
          wheelchair_type: wheelchair.type,
          wheelchair_brand: wheelchair.brand,
          wheelchair_width: wheelchair.width,
          wheelchair_length: wheelchair.length,
          created_at: timestamp,
          updated_at: timestamp,
        })
      )
    );

    // Extract user ID from response(s)
    // Backend returns: { id: number, wheelchair_type: string, wheelchair_brand: string, ... }
    // Use the first response's ID (or the latest if multiple wheelchairs)
    if (responses && responses.length > 0) {
      const firstResponse = responses[0];
      // Backend response has 'id' field directly
      const userId = firstResponse?.id;
      if (userId && typeof userId === 'number') {
        return userId;
      }
    }

    return null;
  }

  /**
   * Create a backend user with empty wheelchair fields (e.g. onboarding skipped).
   * Ensures a stable userId for routing/feedback without wheelchair data.
   */
  async createPlaceholderUser(): Promise<number | null> {
    const timestamp = new Date().toISOString();
    const response = await apiClient.post('/user', {
      wheelchair_type: '',
      wheelchair_brand: '',
      wheelchair_width: 0,
      wheelchair_length: 0,
      created_at: timestamp,
      updated_at: timestamp,
    });
    const userId = (response as { id?: number })?.id;
    if (userId) {
      return userId;
    }
    return null;
  }
  
  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<UserPreferences> {
    return apiClient.get('/user/preferences');
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(preferences: UserPreferences): Promise<UserPreferences> {
    return apiClient.put('/user/preferences', preferences);
  }

  /**
   * Add wheelchair specification
   */
  async addWheelchair(wheelchair: WheelchairSpecs): Promise<WheelchairSpecs> {
    return apiClient.post('/user/wheelchairs', wheelchair);
  }

  /**
   * Update wheelchair specification
   */
  async updateWheelchair(id: string, wheelchair: Partial<WheelchairSpecs>): Promise<WheelchairSpecs> {
    return apiClient.put(`/user/wheelchairs/${id}`, wheelchair);
  }

  /**
   * Delete wheelchair specification
   */
  async deleteWheelchair(id: string): Promise<void> {
    return apiClient.delete(`/user/wheelchairs/${id}`);
  }

  // ==================== Routes ====================
  
  /**
   * Initialize route generation via backend
   * POST /routing/{userId}/routes/init
   */
  async initRoute(userId: number, routeData: {
    start_lat: number;
    start_lon: number;
    end_lat: number;
    end_lon: number;
    start_address: string;
    end_address: string;
  }): Promise<any> {
    // 30 minutes = 1,800,000 milliseconds
    const TIMEOUT_30_MINUTES = 30 * 60 * 1000;
    return apiClient.post(`/routing/${userId}/routes/init`, routeData, {
      timeout: TIMEOUT_30_MINUTES,
    });
  }

  /**
   * Get accessible route
   */
  async getAccessibleRoute(request: RouteRequest): Promise<RouteResponse> {
    return apiClient.post('/routes/accessible', request);
  }

  /**
   * Get alternative routes
   */
  async getAlternativeRoutes(request: RouteRequest): Promise<RouteResponse> {
    return apiClient.post('/routes/alternatives', request);
  }

  /**
   * Get saved routes for user
   */
  async getSavedRoutes(): Promise<Route[]> {
    return apiClient.get('/routes/saved');
  }

  /**
   * Save a route
   */
  async saveRoute(route: Route): Promise<Route> {
    return apiClient.post('/routes/save', route);
  }

  /**
   * Delete a saved route
   */
  async deleteSavedRoute(routeId: string): Promise<void> {
    return apiClient.delete(`/routes/${routeId}`);
  }

  /**
   * Get route history
   */
  async getRouteHistory(limit: number = 10): Promise<Route[]> {
    return apiClient.get(`/routes/history?limit=${limit}`);
  }

  // ==================== Route Feedback ====================
  
  /**
   * Get route comparison data
   * GET /routing/{userId}/routes/{requestId}/compare
   */
  async compareRoute(userId: number, requestId: number): Promise<any[]> {
    return apiClient.get(`/routing/${userId}/routes/${requestId}/compare`);
  }

  /**
   * Reset route state on backend
   * POST /routing/{userId}/routes/{requestId}/reset
   */
  async resetRoute(userId: number, requestId: number): Promise<void> {
    return apiClient.post(`/routing/${userId}/routes/${requestId}/reset`);
  }

  /**
   * Optimize route
   * POST /routing/{userId}/routes/{requestId}/optimize
   */
  async optimizeRoute(
    userId: number,
    requestId: number,
    payload: {
      objective: number; // 0=length, 1=crossing, 2=walk, 3=bike
      direction: string; // "+" to increase, "-" to decrease
    }
  ): Promise<any> {
    // 30 minutes = 1,800,000 milliseconds
    const TIMEOUT_30_MINUTES = 30 * 60 * 1000;
    return apiClient.post(`/routing/${userId}/routes/${requestId}/optimize`, payload, {
      timeout: TIMEOUT_30_MINUTES,
    });
  }

  /**
   * Submit route feedback
   * POST /routing/{userId}/routes/{requestId}/feedback
   */
  async submitRouteFeedback(
    userId: number,
    requestId: number,
    feedback: {
      rating: number;
      labels: string; // Comma-separated string of labels/tags
    }
  ): Promise<void> {
    return apiClient.post(`/routing/${userId}/routes/${requestId}/feedback`, feedback);
  }

  /**
   * Get user's feedback history
   */
  async getFeedbackHistory(): Promise<RouteFeedback[]> {
    return apiClient.get('/routes/feedback/history');
  }

  // ==================== Accessibility Data ====================
  
  /**
   * Get accessibility information for a location
   */
  async getLocationAccessibility(lat: number, lng: number, radius: number = 100): Promise<any> {
    return apiClient.get(`/accessibility/location?lat=${lat}&lng=${lng}&radius=${radius}`);
  }

  /**
   * Report accessibility issue
   */
  async reportAccessibilityIssue(data: {
    lat: number;
    lng: number;
    issueType: string;
    description: string;
    photos?: File[];
  }): Promise<void> {
    // If photos are included, use multipart form data
    if (data.photos && data.photos.length > 0) {
      const formData = new FormData();
      formData.append('lat', data.lat.toString());
      formData.append('lng', data.lng.toString());
      formData.append('issueType', data.issueType);
      formData.append('description', data.description);
      
      data.photos.forEach((photo, index) => {
        formData.append(`photo_${index}`, photo);
      });

      return apiClient.post('/accessibility/report', formData);
    } else {
      return apiClient.post('/accessibility/report', {
        lat: data.lat,
        lng: data.lng,
        issueType: data.issueType,
        description: data.description,
      });
    }
  }

  /**
   * Get community-reported accessibility issues
   */
  async getAccessibilityIssues(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): Promise<any[]> {
    return apiClient.get('/accessibility/issues', {
      params: bounds,
    });
  }

  // ==================== Statistics & Analytics ====================
  
  /**
   * Get user statistics
   */
  async getUserStatistics(): Promise<{
    totalRoutes: number;
    totalDistance: number;
    totalTime: number;
    favoriteLocations: any[];
  }> {
    return apiClient.get('/user/statistics');
  }

  /**
   * Track route usage
   */
  async trackRouteUsage(routeId: string, startTime: Date, endTime: Date): Promise<void> {
    return apiClient.post('/analytics/route-usage', {
      routeId,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
    });
  }
}

export const backendApi = new BackendApiService();
export default backendApi;

